import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Badge,
  Alert,
  InputGroup,
  Spinner,
  Table,
  Pagination,
  ButtonGroup,
  ToggleButton,
  Modal
} from 'react-bootstrap';
import { apifetch } from '../services/interceptors';
import {
  FaThumbsUp,
  FaThumbsDown,
  FaUser,
  FaCalendar,
  FaBell,
  FaComment,
  FaFilter,
  FaSearch,
  FaSync,
  FaPlus,
  FaTable,
  FaThLarge,
  FaClock,
  FaEdit
} from 'react-icons/fa';
import { useTheme } from "../ThemeContext";
import './FeatureRequests.css';

const FeatureRequestsList = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    status: '',
    sort: 'most_votes'
  });
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newRequest, setNewRequest] = useState({
    title: '',
    description: '',
    category: '',
    priority: 'medium'
  });
  const { isDarkMode } = useTheme();

  const categories = ['API', 'Bug', 'Integration', 'Performance', 'Security', 'User Interface', 'others'];
  const statuses = ['new', 'in_progress', 'completed', 'rejected'];
  const priorities = ['low', 'medium', 'high', 'critical'];
  const sortOptions = [
    { value: 'most_votes', label: 'Most Votes' },
    { value: 'newest', label: 'Newest' },
    { value: 'oldest', label: 'Oldest' },
    { value: 'most_commented', label: 'Most Comments' },
    { value: 'priority', label: 'Priority' }
  ];

  const fetchRequests = async (page = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams({
        page,
        ...(filters.search && { search: filters.search }),
        ...(filters.category && { category: filters.category }),
        ...(filters.status && { status: filters.status }),
        ...(filters.sort && { sort: filters.sort })
      });

      const response = await apifetch(`s1/api/requests/${page}?${queryParams}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (response.ok) {
        setRequests(data.results || []);
        setTotalPages(data.pagination?.totalPages || 1);
        setCurrentPage(page);
      } else {
        setError(data.error || 'Failed to fetch requests');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests(1);
  }, [filters]);

  const handleVote = async (requestId, voteType) => {
    try {
      const token = localStorage.getItem('token');
      const response = await apifetch(`s1/api/requests/${requestId}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ voteType }),
      });

      const data = await response.json();
      if (response.ok) {
        setRequests(prev => prev.map(req => 
          req.id === requestId ? { ...req, votes: data.request.votes } : req
        ));
      } else {
        setError(data.error || 'Failed to submit vote');
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleSubscribe = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await apifetch(`s1/api/requests/${requestId}/subscribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (response.ok) {
        setRequests(prev => prev.map(req => 
          req.id === requestId ? { ...req, isSubscribed: !req.isSubscribed } : req
        ));
      } else {
        setError(data.error || 'Failed to update subscription');
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleAddComment = async (requestId) => {
    if (!commentText.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const response = await apifetch(`s1/api/requests/${requestId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ text: commentText }),
      });

      const data = await response.json();
      if (response.ok) {
        setRequests(prev => prev.map(req => 
          req.id === requestId ? { 
            ...req, 
            commentCount: (req.commentCount || 0) + 1,
            recent_comments: [data.comment, ...(req.recent_comments || []).slice(0, 2)]
          } : req
        ));
        setCommentText('');
      } else {
        setError(data.error || 'Failed to add comment');
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleCreateRequest = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await apifetch(`s1/api/requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newRequest),
      });

      const data = await response.json();
      if (response.ok) {
        setShowCreateModal(false);
        setNewRequest({ title: '', description: '', category: '', priority: 'medium' });
        fetchRequests(1);
      } else {
        setError(data.error || 'Failed to create request');
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return `${Math.floor(diffInSeconds / 2592000)}mo ago`;
  };

  const getPriorityBadgeVariant = (priority) => {
    switch (priority) {
      case 'critical': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'primary';
      case 'rejected': return 'danger';
      default: return 'secondary';
    }
  };

  const renderPagination = () => {
    let items = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + 4);
    
    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => fetchRequests(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="justify-content-center mt-4">
        <Pagination.First  onClick={() => fetchRequests(1)} disabled={currentPage === 1} />
        <Pagination.Prev onClick={() => fetchRequests(currentPage - 1)} disabled={currentPage === 1} />
        {items}
        <Pagination.Next
    onClick={() => currentPage < totalPages? null :()=> fetchRequests(currentPage + 1)} 
    disabled={currentPage === totalPages} 
  />

  <Pagination.Last 
    onClick={() => currentPage < totalPages && fetchRequests(totalPages)} 
    disabled={currentPage === totalPages} 
  />
      </Pagination>
    );
  };

  return (
    <Container fluid className={`feature-requests-container ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="mb-0">Feature Requests</h2>
            <div className="d-flex gap-2">
          
              <Button
                variant="primary"
                onClick={() => setShowCreateModal(true)}
                className="d-flex align-items-center"
              >
                <FaPlus className="me-1" />
                New Request
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="mb-4" bg={isDarkMode ? 'dark' : 'light'} text={isDarkMode ? 'white' : 'dark'}>
        <Card.Body>
          <Row>
            <Col md={4} className="mb-3">
              <Form.Group>
                <InputGroup>
                  <InputGroup.Text>
                    <FaSearch color={isDarkMode?"white":'black'} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search requests..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className={isDarkMode ? 'bg-dark text-light border-secondary placeholder-light' : ''}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={2} className="mb-3">
              <Form.Group>
                <Form.Select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                >
                  <option value="">All Categories</option>
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2} className="mb-3">
              <Form.Group>
                <Form.Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                >
                  <option value="">All Statuses</option>
                  {statuses.map(status => (
                    <option key={status} value={status}>
                      {status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2} className="mb-3">
              <Form.Group>
                <Form.Select
                  value={filters.sort}
                  onChange={(e) => handleFilterChange('sort', e.target.value)}
                  className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                >
                  {sortOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2} className="mb-3">
              <Button
                variant="outline-secondary"
                onClick={() => fetchRequests(1)}
                className="w-100 d-flex align-items-center justify-content-center"
                disabled={loading}
              >
                <FaSync className="me-1" />
                Refresh
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
          {error}
        </Alert>
      )}

      {/* Content */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading feature requests...</p>
        </div>
      ) : requests.length === 0 ? (
        <Alert variant="info" className="text-center">
          No feature requests found. {filters.search || filters.category || filters.status ? 'Try adjusting your filters.' : '!'}
        </Alert>
      ) : viewMode === 'card' ? (
        <>
          <Row>
            {requests.map((request) => (
              <Col key={request.id} lg={6} xl={4} className="mb-4">
                <Card className="h-100 feature-request-card" bg={isDarkMode ? 'dark' : 'light'} text={isDarkMode ? 'white' : 'dark'}>
                  <Card.Body className="d-flex flex-column">
                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div className="flex-grow-1">
                        <h6 className="card-title mb-2">{request.title}</h6>
                        <div className="d-flex flex-wrap gap-1 mb-2">
                          <Badge bg="primary" className="text-capitalize">
                            {request.category}
                          </Badge>
                          <Badge bg={getStatusBadgeVariant(request.status)} className="text-capitalize">
                            {request.status.replace('_', ' ')}
                          </Badge>
                          {request.priority && (
                            <Badge bg={getPriorityBadgeVariant(request.priority)} className="text-capitalize">
                              {request.priority}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Meta Information */}
                    <div className="d-flex align-items-center  small mb-3">
                      <div className="d-flex align-items-center me-3">
                        <FaUser className="me-1" size={12} />
                        <span>{request.submitter}</span>
                      </div>
                      <div className="d-flex align-items-center me-3">
                        <FaCalendar className="me-1" size={12} />
                        <span>{formatDate(request.created_at)}</span>
                      </div>
                      <div className="d-flex align-items-center">
                        <FaClock className="me-1" size={12} />
                        <span>Updated {getTimeAgo(request.updated_at || request.created_at)}</span>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="card-text flex-grow-1 mb-3">{request.description}</p>

                    {/* Actions */}
                    <div className="d-flex justify-content-between align-items-center mt-auto">
                      <div className="d-flex align-items-center gap-2">
                        <Button
                          variant={request.isSubscribed ? 'warning' : 'outline-warning'}
                          size="sm"
                          onClick={() => handleSubscribe(request.id)}
                          className="d-flex align-items-center"
                        >
                          <FaBell className="me-1" />
                          {request.isSubscribed ? 'Subscribed' : 'Notify'}
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => setExpandedRequest(expandedRequest === request.id ? null : request.id)}
                          className="d-flex align-items-center"
                        >
                          <FaComment className="me-1" />
                          {request.commentCount || 0}
                        </Button>
                      </div>
                      <div className="d-flex align-items-center gap-1">
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleVote(request.id, 'up')}
                          className="d-flex align-items-center"
                        >
                          <FaThumbsUp className="me-1" />
                          {request.votes || 0}
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleVote(request.id, 'down')}
                          className="d-flex align-items-center"
                        >
                          <FaThumbsDown />
                        </Button>
                      </div>
                    </div>

                    {/* Comments Section */}
                    {expandedRequest === request.id && (
                      <div className="mt-3 pt-3 border-top">
                        <h6 className="mb-3">Comments</h6>
                        
                        {/* Recent Comments */}
                        {request.recent_comments && request.recent_comments.length > 0 ? (
                          <div className="mb-3">
                            {request.recent_comments.map((comment) => (
                              <div key={comment.id} className="mb-2 p-2 bg-secondary rounded">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <strong className="small">{comment.author}</strong>
                                  <small className="">
                                    {getTimeAgo(comment.created_at)}
                                  </small>
                                </div>
                                <p className="mb-0 small">{comment.text}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className=" small">No comments yet.</p>
                        )}

                        {/* Add Comment */}
                        <Form.Group>
                          <InputGroup>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              placeholder="Add a comment..."
                              value={commentText}
                              onChange={(e) => setCommentText(e.target.value)}
                              className={isDarkMode ? 'bg-dark text-light' : ''}
                            />
                          </InputGroup>
                          <div className="d-flex justify-content-end mt-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleAddComment(request.id)}
                              disabled={!commentText.trim()}
                            >
                              Post Comment
                            </Button>
                          </div>
                        </Form.Group>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
          {renderPagination()}
        </>
      ) : (
        <>
          <Card bg={isDarkMode ? 'dark' : 'light'} text={isDarkMode ? 'white' : 'dark'}>
            <div className="table-responsive">
              <Table striped bordered hover variant={isDarkMode ? 'dark' : ''} className="mb-0">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th>Votes</th>
                    <th>Comments</th>
                    <th>Submitted By</th>
                    <th>Created</th>
                    <th>Updated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((request) => (
                    <React.Fragment key={request.id}>
                      <tr>
                        <td>
                          <div className="fw-semibold">{request.title}</div>
                          <div className="small ">{request.description.substring(0, 60)}...</div>
                        </td>
                        <td>
                          <Badge bg="primary" className="text-capitalize">
                            {request.category}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={getStatusBadgeVariant(request.status)} className="text-capitalize">
                            {request.status.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td>
                          {request.priority && (
                            <Badge bg={getPriorityBadgeVariant(request.priority)} className="text-capitalize">
                              {request.priority}
                            </Badge>
                          )}
                        </td>
                        <td className="text-center">{request.votes || 0}</td>
                        <td className="text-center">{request.commentCount || 0}</td>
                        <td>{request.submitter}</td>
                        <td>{formatDate(request.created_at)}</td>
                        <td>{getTimeAgo(request.updated_at || request.created_at)}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleVote(request.id, 'up')}
                              title="Upvote"
                            >
                              <FaThumbsUp />
                            </Button>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => setExpandedRequest(expandedRequest === request.id ? null : request.id)}
                              title="Comments"
                            >
                              <FaComment />
                            </Button>
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={() => handleSubscribe(request.id)}
                              title="Subscribe"
                            >
                              <FaBell />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {expandedRequest === request.id && (
                        <tr>
                          <td colSpan="10">
                            <div className="p-3 bg-secondary rounded">
                              <h6 className="mb-3">Comments</h6>
                              
                              {/* Recent Comments */}
                              {request.recent_comments && request.recent_comments.length > 0 ? (
                                <div className="mb-3">
                                  {request.recent_comments.map((comment) => (
                                    <div key={comment.id} className="mb-2 p-2 bg-dark rounded">
                                      <div className="d-flex justify-content-between align-items-center mb-1">
                                        <strong className="small">{comment.author}</strong>
                                        <small className="">
                                          {getTimeAgo(comment.created_at)}
                                        </small>
                                      </div>
                                      <p className="mb-0 small">{comment.text}</p>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className=" small">No comments yet.</p>
                              )}

                              {/* Add Comment */}
                              <Form.Group>
                                <InputGroup>
                                  <Form.Control
                                    as="textarea"
                                    rows={2}
                                    placeholder="Add a comment..."
                                    value={commentText}
                                    onChange={(e) => setCommentText(e.target.value)}
                                    className={isDarkMode ? 'bg-dark text-light' : ''}
                                  />
                                </InputGroup>
                                <div className="d-flex justify-content-end mt-2">
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => handleAddComment(request.id)}
                                    disabled={!commentText.trim()}
                                  >
                                    Post Comment
                                  </Button>
                                </div>
                              </Form.Group>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card>
          {renderPagination()}
        </>
      )}

      {/* Create Request Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered>
        <Modal.Header closeButton className={isDarkMode ? 'bg-dark text-light' : ''}>
          <Modal.Title>Create New Feature Request</Modal.Title>
        </Modal.Header>
        <Modal.Body className={isDarkMode ? 'bg-dark text-light' : ''}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter feature title"
                value={newRequest.title}
                onChange={(e) => setNewRequest({...newRequest, title: e.target.value})}
                className={isDarkMode ? 'bg-dark text-light' : ''}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Describe the feature in detail"
                value={newRequest.description}
                onChange={(e) => setNewRequest({...newRequest, description: e.target.value})}
                className={isDarkMode ? 'bg-dark text-light' : ''}
              />
            </Form.Group>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    value={newRequest.category}
                    onChange={(e) => setNewRequest({...newRequest, category: e.target.value})}
                    className={isDarkMode ? 'bg-dark text-light' : ''}
                  >
                    <option value="">Select Category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Select
                    value={newRequest.priority}
                    onChange={(e) => setNewRequest({...newRequest, priority: e.target.value})}
                    className={isDarkMode ? 'bg-dark text-light' : ''}
                  >
                    {priorities.map(priority => (
                      <option key={priority} value={priority}>
                        {priority.charAt(0).toUpperCase() + priority.slice(1)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className={isDarkMode ? 'bg-dark' : ''}>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateRequest}>
            Create Request
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default FeatureRequestsList;